import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocale } from '@/lib/utils';
import { useStripe } from '@stripe/react-stripe-js';
import { useStripeOnMount } from '@/provider/stripeLoader';
import { ArrowLeft } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  fetchPrices,
  fetchPaymentMethods,
  fetchSubscription,
  fetchUsage,
  fetchUpgradePreview,
  fetchDowngradeImpactPreview,
  setPaymentMethodDefault,
  upgradeSubscription,
  fetchInvoices,
  createSetupIntent,
} from '@/features/billing/billingThunks';
import {
  selectPrices,
  selectPaymentMethods,
  selectSubscription,
  selectUpgradePreview,
  selectUpgradeLoading,
  selectUpgradeError,
  selectDowngradeImpactPreview,
  selectDowngradeImpactPreviewState,
} from '@/features/billing/billingSlice';
import { useSubmitLock } from '@/hooks/useSubmitLock';
import Title from '@/components/ui/Title';
import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import PlanSection from '@/components/billing/PlanSection';
import BillingIntervalSelector from '@/components/billing/BillingIntervalSelector';
import UpgradePaymentMethodSelector from '@/components/billing/UpgradePaymentMethodSelector';
import UpgradePaymentForm from '@/components/pages/configuration/subscription/UpgradePaymentForm';
import {
  formatPrice,
  getPlanDisplayName,
  getPriceByInterval,
  getPricingInfo,
  isPlanDowngrade,
} from '@/components/billing/usageExpiredUtils';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { selectUser } from '@/features/auth/selectors';
import { toast } from 'react-toastify';
import DowngradeImpactSummary from './DowngradeImpactSummary';
import BillingAddressForm, { type BillingAddressFormRef } from './BillingAddressForm';

type ChangePlanDialogProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
};

export default function ChangePlanDialog({
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: ChangePlanDialogProps = {}) {
  const { t, i18n } = useTranslation('billing');
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const subscription = useAppSelector(selectSubscription);
  const prices = useAppSelector(selectPrices);
  const workspace = useAppSelector((s) => s.workspace.currentWorkspace);
  const workspaceId = useAppSelector((s) => s.workspace.currentWorkspace?.id);
  const upgradePreview = useAppSelector(selectUpgradePreview);
  const upgradeLoading = useAppSelector(selectUpgradeLoading);
  const upgradeError = useAppSelector(selectUpgradeError);
  const pricesLoading = useAppSelector((s) => s.billing.prices.loading);
  const upgradePreviewLoading = useAppSelector((s) => s.billing.upgradePreview.loading);
  const downgradeImpactPreview = useAppSelector(selectDowngradeImpactPreview);
  const downgradeImpactPreviewState = useAppSelector(selectDowngradeImpactPreviewState);
  const paymentMethods = useAppSelector(selectPaymentMethods);
  const user = useAppSelector(selectUser);

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const dialogOpen = isControlled ? controlledOpen : internalOpen;
  useStripeOnMount(dialogOpen);
  const setDialogOpen = (open: boolean) => {
    if (isControlled) onOpenChange?.(open);
    else setInternalOpen(open);
  };
  type ChangePlanStep = 1 | 2 | 'downgrade';
  const [step, setStep] = useState<ChangePlanStep>(1);
  const [selectedPlanKey, setSelectedPlanKey] = useState<string | null>(null);
  const [isYearly, setIsYearly] = useState(true);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const { isLocked: isSavedCardProcessing, runExclusive } = useSubmitLock();
  const billingAddressFormRef = useRef<BillingAddressFormRef>(null);
  const [isBillingAddressValid, setIsBillingAddressValid] = useState(false);
  const isTrial = workspace?.subscriptionStatus === 'NONE';
  const isCancellationScheduled = subscription?.cancelAtPeriodEnd === true;

  const nextPeriodEndFormatted = subscription?.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString(getLocale(), {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  useEffect(() => {
    if (workspaceId)
      dispatch(
        fetchPrices({
          workspaceId,
          ...(workspace?.promoCode && { promoCode: workspace.promoCode }),
        }),
      );
  }, [workspaceId, workspace?.promoCode, dispatch]);

  const selectedPrice = selectedPlanKey
    ? getPriceByInterval(prices, selectedPlanKey, isYearly ? 'year' : 'month')
    : null;
  const priceId = selectedPrice?.id ?? null;
  // Le tarif reconduit après le changement vient du preview Stripe : le catalogue prévisualise une nouvelle
  // souscription et déduirait une promo déjà consommée.
  const selectedReferencePrice = upgradePreview?.nextRecurringAmount ?? 0;
  const selectedPriceDiscountAmount =
    selectedPrice && selectedReferencePrice > 0
      ? Math.max(0, selectedPrice.amount - selectedReferencePrice)
      : 0;

  useEffect(() => {
    if (workspaceId && priceId && step === 2) {
      dispatch(fetchUpgradePreview({ workspaceId, newPriceId: priceId }));
    }
  }, [workspaceId, priceId, step, dispatch]);

  // Le back refuse le downgrade tant que ce récapitulatif n'a pas été affiché puis confirmé.
  useEffect(() => {
    if (workspaceId && priceId && step === 'downgrade') {
      dispatch(fetchDowngradeImpactPreview({ workspaceId, newPriceId: priceId }));
    }
  }, [workspaceId, priceId, step, dispatch]);

  useEffect(() => {
    if (workspaceId && step === 2) {
      dispatch(fetchPaymentMethods(workspaceId));
    }
  }, [workspaceId, step, dispatch]);

  useEffect(() => {
    if (step === 2 && paymentMethods?.length) {
      const defaultCard = paymentMethods.find((pm) => pm.isDefault);
      setSelectedPaymentMethodId(defaultCard?.id ?? paymentMethods[0]?.id ?? null);
      setUseNewCard(false);
    } else {
      setSelectedPaymentMethodId(null);
      setUseNewCard(paymentMethods?.length === 0);
    }
  }, [step, paymentMethods]);

  const handleSelectPlan = (planKey: string) => {
    const isCurrent =
      subscription?.planName && planKey.toLowerCase() === subscription.planName.toLowerCase();
    if (isCurrent) return;
    setSelectedPlanKey(planKey);
    const downgrade = !isTrial && isPlanDowngrade(subscription?.planName, planKey);
    setStep(downgrade ? 'downgrade' : 2);
  };

  const handleBack = () => {
    setStep(1);
    setSelectedPlanKey(null);
    setIsYearly(false);
  };

  const handleConfirmDowngrade = async () => {
    if (!workspaceId || !priceId) return;
    try {
      await dispatch(
        upgradeSubscription({ workspaceId, input: { priceId, downgradeImpactConfirmed: true } }),
      ).unwrap();
      await Promise.all([
        dispatch(fetchSubscription(workspaceId)),
        dispatch(fetchUsage(workspaceId)),
        dispatch(fetchInvoices(workspaceId)),
      ]);
      toast.success(
        t('changePlan.toast.downgradeSuccess', { planKey: getPlanDisplayName(selectedPlanKey) }),
      );
      setDialogOpen(false);
      setStep(1);
      setSelectedPlanKey(null);
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message;
      toast.error(message ?? t('changePlan.toast.changeError'));
    }
  };

  const upgradeWithSavedCard = async () => {
    if (!workspaceId || !priceId) return;
    if (!stripe) {
      toast.error(t('changePlan.toast.notReady'));
      return;
    }

    const saved = await billingAddressFormRef.current?.saveAddress();
    if (saved === false) return;

    const pmToUse = selectedPaymentMethodId ?? paymentMethods?.find((pm) => pm.isDefault)?.id;
    if (!pmToUse) {
      toast.error(t('changePlan.toast.noPaymentMethod'));
      return;
    }

    try {
      await dispatch(setPaymentMethodDefault({ workspaceId, paymentMethodId: pmToUse })).unwrap();

      const setupResult = await dispatch(createSetupIntent(workspaceId)).unwrap();
      const { error: setupError } = await stripe.confirmCardSetup(setupResult.clientSecret, {
        payment_method: pmToUse,
      });

      if (setupError) {
        toast.error(setupError.message ?? t('changePlan.toast.cardValidationError'));
        return;
      }

      const subscriptionResult = await dispatch(
        upgradeSubscription({
          workspaceId,
          input: { priceId },
        }),
      ).unwrap();

      if (subscriptionResult.paymentIntentClientSecret) {
        const { error: paymentError } = await stripe.confirmCardPayment(
          subscriptionResult.paymentIntentClientSecret,
          { payment_method: pmToUse },
        );
        if (paymentError) {
          const alreadySucceeded =
            paymentError.code === 'payment_intent_unexpected_state' &&
            paymentError.payment_intent?.status === 'succeeded';
          if (!alreadySucceeded) {
            toast.error(paymentError.message ?? t('changePlan.toast.paymentFailed'));
            return;
          }
        }
      }

      await Promise.all([
        dispatch(fetchSubscription(workspaceId)),
        dispatch(fetchUsage(workspaceId)),
        dispatch(fetchPaymentMethods(workspaceId)),
        dispatch(fetchInvoices(workspaceId)),
      ]);
      toast.success(t('changePlan.toast.upgradeSuccess'));
      setDialogOpen(false);
      setStep(1);
      setSelectedPlanKey(null);
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message;
      toast.error(message ?? t('changePlan.toast.upgradeError'));
    }
  };

  const handleUpgradeWithSavedCard = () => void runExclusive(upgradeWithSavedCard);

  const handleUpgradeSuccess = () => {
    if (workspaceId) {
      dispatch(fetchSubscription(workspaceId));
      dispatch(fetchUsage(workspaceId));
      dispatch(fetchPaymentMethods(workspaceId));
    }
    setDialogOpen(false);
    setStep(1);
    setSelectedPlanKey(null);
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (open && isCancellationScheduled) return;
    setDialogOpen(open);
    if (!open) {
      setStep(1);
      setSelectedPlanKey(null);
      setIsYearly(true);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
      {!hideTrigger &&
        (isCancellationScheduled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block">
                <Button type="button" disabled>
                  {t('changePlan.trigger')}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{t('changePlan.tooltipCancel')}</TooltipContent>
          </Tooltip>
        ) : (
          <DialogTrigger asChild>
            <Button type="button">{t('changePlan.trigger')}</Button>
          </DialogTrigger>
        ))}

      <DialogContent className="max-w-[1350px] p-4 sm:p-6">
        {step === 1 ? (
          <>
            <DialogHeader className="flex flex-col items-center gap-2">
              <DialogTitle>{t('changePlan.advantagesTitle')}</DialogTitle>
              <DialogDescription className="hidden">
                {t('changePlan.advantagesDescription')}
              </DialogDescription>
            </DialogHeader>
            <PlanSection
              prices={prices}
              isTrial={isTrial}
              isExpired={false}
              onSelectPlan={handleSelectPlan}
              currentPlanName={subscription?.planName}
              isWelyaUser={Boolean(user?.email?.toLowerCase().endsWith('@welya.io'))}
              isYearly={isYearly}
              onIsYearlyChange={setIsYearly}
            />
          </>
        ) : step === 'downgrade' ? (
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleBack}
                disabled={upgradeLoading}
                className="size-6 cursor-pointer"
                aria-label={t('changePlan.downgrade.backAriaLabel')}
              >
                <ArrowLeft className="size-6" />
              </button>
              <DialogTitle className="pb-0">{t('changePlan.downgrade.title')}</DialogTitle>
            </div>

            <div className="p-4 items-center rounded-[20px] bg-[#1F1F1F]/3 flex gap-4">
              <Avatar className="size-12">
                <AvatarImage src={user?.pictureUrl} />
                <AvatarFallback>
                  {user?.firstName?.charAt(0) ?? ''}
                  {user?.lastName?.charAt(0) ?? ''}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="text-xl font-medium truncate">{workspace?.name}</p>
                <p className="text-text-secondary text-base">
                  {t('common.plan')} :{' '}
                  <span className="text-highlighted">{getPlanDisplayName(selectedPlanKey)}</span>
                </p>
              </div>
            </div>

            <p className="text-secondary text-sm">
              {t('changePlan.downgrade.currentSubscriptionPrefix')}{' '}
              <span className="font-medium text-foreground">
                {getPlanDisplayName(subscription?.planName)}
              </span>{' '}
              {nextPeriodEndFormatted
                ? t('changePlan.downgrade.untilDate', { date: nextPeriodEndFormatted })
                : t('changePlan.downgrade.untilEnd')}{' '}
              {t('changePlan.downgrade.changeEffect')}
            </p>

            <DowngradeImpactSummary
              preview={downgradeImpactPreview}
              loading={downgradeImpactPreviewState.loading}
              error={downgradeImpactPreviewState.error}
              onRetry={() =>
                workspaceId && priceId
                  ? dispatch(fetchDowngradeImpactPreview({ workspaceId, newPriceId: priceId }))
                  : undefined
              }
            />

            <DialogFooter className="flex flex-col sm:flex-row w-full gap-4">
              <Button
                type="button"
                variant="outline"
                className="w-full sm:flex-1 h-12"
                onClick={handleBack}
                disabled={upgradeLoading}
              >
                {t('changePlan.downgrade.cancel')}
              </Button>
              <Button
                type="button"
                className="w-full sm:flex-1 h-12"
                onClick={handleConfirmDowngrade}
                disabled={
                  upgradeLoading ||
                  pricesLoading ||
                  downgradeImpactPreviewState.loading ||
                  !downgradeImpactPreview ||
                  !priceId
                }
              >
                {upgradeLoading && <Loader />}
                {upgradeLoading
                  ? t('changePlan.downgrade.confirming')
                  : t('changePlan.downgrade.confirm')}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center justify-center p-1 cursor-pointer"
                aria-label={t('changePlan.payment.backAriaLabel')}
              >
                <ArrowLeft className="size-6" />
              </button>
              <DialogTitle className="text-xl font-semibold pb-0">
                {t('changePlan.payment.title')}
              </DialogTitle>
            </div>

            {/* Workspace */}
            <div className="p-4 rounded-[20px] bg-[#1F1F1F]/3 flex gap-4">
              <Avatar className="size-12">
                <AvatarImage src={user?.pictureUrl} />
                <AvatarFallback>
                  {user?.firstName?.charAt(0) ?? ''}
                  {user?.lastName?.charAt(0) ?? ''}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="text-xl font-medium truncate">{workspace?.name}</p>
                <p className="text-text-secondary text-base">
                  {t('common.plan')} :{' '}
                  <span className="text-highlighted">{getPlanDisplayName(selectedPlanKey)}</span>
                </p>
              </div>
            </div>

            <BillingIntervalSelector
              prices={prices}
              selectedPlanKey={selectedPlanKey}
              isYearly={isYearly}
              onIsYearlyChange={setIsYearly}
            />

            {(pricesLoading || upgradePreviewLoading) && (
              <div className="p-4 border border-gray-200 rounded-lg flex items-center gap-2 text-sm text-secondary">
                <Loader />
                {t('changePlan.payment.loadingPreview')}
              </div>
            )}

            {!pricesLoading && !upgradePreviewLoading && upgradePreview && (
              <div className="p-4 border border-gray-200 rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">
                    {t('changePlan.payment.planPrice')}
                  </span>
                  <span className="text-lg font-semibold">
                    {formatPrice(
                      selectedPrice?.amount ?? upgradePreview.nextRecurringAmount,
                      upgradePreview.currency,
                    )}
                  </span>
                </div>
                {selectedPriceDiscountAmount > 0 && selectedPrice && workspace?.promoCode ? (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">
                        {t('promoCode.applied')}{' '}
                        <span className="font-mono">({workspace.promoCode})</span>
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        -{formatPrice(selectedPriceDiscountAmount, selectedPrice.currency)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">
                        {t('changePlan.payment.nextPayment')}
                      </span>
                      <span className="text-lg font-semibold">
                        {formatPrice(selectedReferencePrice, upgradePreview.currency)}
                      </span>
                    </div>
                  </>
                ) : null}
                <p className="text-sm text-gray-600">
                  {t('changePlan.payment.nextInvoiceDate', {
                    date: new Date(upgradePreview.nextInvoiceDate).toLocaleDateString(
                      i18n.language,
                    ),
                  })}
                </p>
                {(() => {
                  const proration = upgradePreview.immediateAmount - selectedReferencePrice;
                  return (
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">
                        {t('changePlan.payment.proration')}
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        {proration > 0 ? '+' : ''}
                        {formatPrice(proration, upgradePreview.currency)}
                      </span>
                    </div>
                  );
                })()}
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-base font-semibold text-gray-800">
                      {t('changePlan.payment.duNow')}
                    </span>
                    <span className="text-xl font-bold text-green-600">
                      {formatPrice(upgradePreview.immediateAmount, upgradePreview.currency)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {upgradeError && <p className="text-sm text-destructive">{upgradeError}</p>}

            <div className="space-y-4">
              <Title level={2}>{t('changePlan.payment.billingAddress')}</Title>
              <BillingAddressForm
                ref={billingAddressFormRef}
                workspaceId={workspaceId}
                embedded
                onValidityChange={setIsBillingAddressValid}
              />
            </div>

            <div className="space-y-6">
              <Title level={2}>{t('common.paymentByCard')}</Title>

              {paymentMethods && paymentMethods.length > 0 && !useNewCard && (
                <>
                  <UpgradePaymentMethodSelector
                    paymentMethods={paymentMethods}
                    selectedId={selectedPaymentMethodId}
                    onSelect={setSelectedPaymentMethodId}
                    disabled={upgradeLoading || isSavedCardProcessing}
                  />
                  <Button
                    onClick={handleUpgradeWithSavedCard}
                    disabled={
                      upgradeLoading ||
                      isSavedCardProcessing ||
                      pricesLoading ||
                      upgradePreviewLoading ||
                      !upgradePreview ||
                      !priceId ||
                      !stripe ||
                      !isBillingAddressValid
                    }
                    className="w-full"
                  >
                    {(upgradeLoading || isSavedCardProcessing) && <Loader />}
                    {upgradeLoading || isSavedCardProcessing
                      ? t('changePlan.payment.processing')
                      : t('changePlan.payment.confirm')}
                  </Button>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-gray-500">{t('common.or')}</span>
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-4">
                {useNewCard && paymentMethods && paymentMethods.length > 0 && (
                  <button
                    type="button"
                    className="text-sm text-highlighted cursor-pointer"
                    onClick={() => setUseNewCard(false)}
                  >
                    {t('common.useSavedCard')}
                  </button>
                )}

                <div>
                  {useNewCard || paymentMethods?.length === 0 ? (
                    <UpgradePaymentForm
                      priceId={priceId}
                      onBeforePayment={async () => {
                        const ok = await billingAddressFormRef.current?.saveAddress();
                        return ok ?? true;
                      }}
                      amount={
                        upgradePreview?.immediateAmount ??
                        (selectedPlanKey
                          ? (() => {
                              const info = getPricingInfo(prices, selectedPlanKey);
                              return info
                                ? isYearly
                                  ? info.yearlyAmount
                                  : info.monthlyAmount
                                : null;
                            })()
                          : null)
                      }
                      workspaceId={workspaceId}
                      disabled={upgradeLoading || !isBillingAddressValid}
                      onSuccess={handleUpgradeSuccess}
                    />
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={upgradeLoading || isSavedCardProcessing}
                      onClick={() => setUseNewCard(true)}
                    >
                      {t('common.payWithNewCard')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
